import { Fragment, useState, useEffect, useContext, useMemo } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { SidebarLayout } from "V2.0/common/sidebarLayout";
import { SidebarNavigation } from "./Sidebar";
import { CreateNewWorkspace } from "V2.0/HomePages/Workspaces/Create_new";
import { manageSubscription } from "V2.0/service-worker/registerAndSubscribe";

import useCustomers from "V2.0/hooks/useCustomers";
import { useAccountContext } from "V2.0/Context/AccountContext";
import useCustomer from "V2.0/hooks/useCustomer";
import useWorkspaceSettings from "V2.0/hooks/useWorkspaceSettings";
import { useThirdPartyContext } from "V2.0/Context/ThirdPartyContext";
import { useAssistantsContext } from "V2.0/Context/AssistantsContext";
import useRoleAccessBasedNavigation from "V2.0/hooks/useRoleAccessBasedNavigation";
import { UserAccessLevel } from "V2.0/models/UserProfile";

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [reload, setReload] = useState(false);

  const [activeProject, setActiveProject] = useState(undefined);
  const [projectId, setProjectId] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();

  const { workspaceSettings: settings, fetchWorkspaceSettings: fetchSettings } =
    useWorkspaceSettings(projectId);

  const {
    workspaces,
    company,
    isAccountLoading,
    fetchWorkspaces,
    customer: user,
  } = useAccountContext();
  const { thirdpartyData, fetchThirdpartyData, updateThirdpartyData } =
    useThirdPartyContext();

  const { assistants, isAssistantsLoading, fetchAssistants } =
    useAssistantsContext();
  const { roleAccessBasedNavigationItems, allowedLocations, allowedSections } =
    useRoleAccessBasedNavigation(user, company);

  const { customers, isCustomersLoading } = useCustomers(company?._id);

  const { userId } = user;

  const workspacesRecord = useMemo(() => {
    if (workspaces.length > 0) {
      return workspaces.reduce((acc, workspace, index) => {
        return { ...acc, [index]: workspace._id };
      });
    }
  }, [workspaces]);

  const updateActiveProject = (projectIndex) => {
    const projectId = workspaces[projectIndex]?._id;

    localStorage.setItem("activeProject", projectIndex);
    localStorage.setItem("lastActiveProjectId", projectId);

    setActiveProject(projectIndex);
    setProjectId(projectId);

    fetchThirdpartyData(projectId);
    fetchAssistants(projectId);
  };

  //   useEffect(() => {
  //     return () => {
  //       cleanupResources();
  //     };
  //   }, [location.pathname]);

  useEffect(() => {
    if (!projectId) return;

    const searchParams = new URLSearchParams(location.search);
    const currentProjectId = searchParams.get("projectId");

    if (currentProjectId === projectId) return;

    function getRootPath(pathname) {
      const [, first] = pathname.split("/");
      return first ? `/${first}` : "/";
    }

    searchParams.set("projectId", projectId);

    navigate(
      {
        pathname: getRootPath(location.pathname),
        search: `?${searchParams.toString()}`,
      },
      { replace: true },
    );
  }, [projectId]);

  useEffect(() => {
    const isWorkspacesAvailable = workspaces.length > 0;

    if (!isWorkspacesAvailable) {
      return;
    }

    const projectIdFromSearchParam = searchParams.get("projectId");

    if (projectIdFromSearchParam) {
      const projectIndex = workspaces.findIndex(
        (project) => project._id === projectIdFromSearchParam,
      );
      if (projectIndex != -1) {
        updateActiveProject(projectIndex);
      } else {
        updateActiveProject(0);
      }

      return;
    }

    const activeProjectIndex = localStorage.getItem("activeProject");
    const lastActiveProjectId = localStorage.getItem("lastActiveProjectId");

    const isActiveProjectIndexEmpty = activeProjectIndex === null;

    const isActiveProjectIndexExistsInAvailableProjects =
      workspaces.length > Number(activeProjectIndex);

    if (
      isActiveProjectIndexEmpty ||
      !isActiveProjectIndexExistsInAvailableProjects
    ) {
      updateActiveProject(0);
      return;
    }

    if (
      isActiveProjectIndexExistsInAvailableProjects &&
      lastActiveProjectId === null
    ) {
      updateActiveProject(activeProjectIndex);
      return;
    }

    if (isActiveProjectIndexExistsInAvailableProjects && lastActiveProjectId) {
      const activeProjectIndexId = workspacesRecord[activeProjectIndex];

      if (activeProjectIndexId === lastActiveProjectId) {
        updateActiveProject(activeProjectIndex);
      } else {
        const projectsIndex = workspaces.findIndex(
          (project) => project._id === lastActiveProjectId,
        );
        if (projectsIndex != -1) {
          updateActiveProject(projectsIndex);
        } else {
          updateActiveProject(0);
        }
      }
    }
  }, [workspacesRecord]);

  useEffect(() => {
    if (!projectId || !userId) {
      return;
    }

    manageSubscription(projectId, userId);
  }, [projectId, userId]);

  useEffect(() => {
    if (isAccountLoading || user.isLoading) {
      return;
    }

    if (workspaces.length === 0 && user?.isOwner) {
      setIsOpen(true);
    }
  }, [workspaces, isAccountLoading, user]);

  if (isAccountLoading || user.isLoading) {
    return null;
  }

  if (!isAccountLoading && !user.isLoading && workspaces.length === 0 && !user?.isOwner) {
    return <Navigate to="/logout-agent" />;
  }

  return (
    <>
      <SidebarLayout
        sidebar={
          <SidebarNavigation
            setIsOpen={setIsOpen}
            company={company}
            projects={workspaces}
            activeProject={activeProject}
            setActiveProject={updateActiveProject}
            firstNameShort={""}
            user={user}
            loading={isAccountLoading || user?.isLoading}
            roleAccessBasedNavigationItems={roleAccessBasedNavigationItems}
          />
        }
      >
        <Outlet
          context={{
            company,
            customers,
            isCustomersLoading,
            projects: workspaces,
            fetchProjects: fetchWorkspaces,
            activeProject,
            projectId,
            searchParams,
            setSearchParams,
            settings,
            fetchSettings,
            reload,
            setReload,
            user,
            thirdpartyData,
            fetchThirdpartyData,
            updateThirdpartyData,
            assistants,
            isAssistantsLoading,
            fetchAssistants,
            isAccountLoading,
            allowedLocations,
            allowedSections,
            isViewOnly:
              user.userAccessLevel === UserAccessLevel.WHITELABELVIEWER,
          }}
        />
      </SidebarLayout>
      {isOpen && (
        <CreateNewWorkspace
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          projects={workspaces}
          company={company}
          fetchProjects={fetchWorkspaces}
          setProjectId={setProjectId}
        />
      )}
    </>
  );
}
